import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const OPS_BASE  = `${BACKEND_URL}/api/ops`;
const OPS_KEY   = 'ops_tk';
const OPS_DEVICE_KEY = 'ops_device_token';

// Always read token fresh from sessionStorage on every call.
// This survives HMR reloads, page navigations, and module re-evaluations.
const getToken = () => sessionStorage.getItem(OPS_KEY);
const setToken = (t) => sessionStorage.setItem(OPS_KEY, t);
const removeToken = () => sessionStorage.removeItem(OPS_KEY);
// Trusted-device token persists across sessions (localStorage) for its 12h window.
const getDeviceToken = () => localStorage.getItem(OPS_DEVICE_KEY);
const setDeviceToken = (t) => t && localStorage.setItem(OPS_DEVICE_KEY, t);

// Single request helper — token injected on every call, never stale
function opsRequest(method, path, data = null, params = null) {
  const token = getToken();
  return axios({
    method,
    url: path.startsWith('http') ? path : `${OPS_BASE}${path}`,
    data: data || undefined,
    params: params || undefined,
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

const OpsAuthContext = createContext(null);

export function OpsAuthProvider({ children }) {
  const [opsUser, setOpsUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    // Verify existing session
    opsRequest('GET', '/auth/me')
      .then(r => setOpsUser(r.data))
      .catch(() => { removeToken(); setOpsUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    // Step 1: password. Returns either a session token (trusted device) or an MFA challenge.
    const res = await axios.post(`${OPS_BASE}/auth/login`,
      { email, password, device_token: getDeviceToken() || undefined },
      { withCredentials: true }
    );
    if (res.data.mfa_required) {
      return { mfaRequired: true, challenge: res.data.challenge, message: res.data.message };
    }
    const { token, ops_user } = res.data;
    setToken(token);
    setOpsUser(ops_user);
    return { mfaRequired: false, ops_user };
  };

  const verify2fa = async (challenge, code, rememberDevice) => {
    const res = await axios.post(`${OPS_BASE}/auth/verify-2fa`,
      { challenge, code, remember_device: !!rememberDevice },
      { withCredentials: true }
    );
    const { token, ops_user, device_token } = res.data;
    if (device_token) setDeviceToken(device_token);
    setToken(token);
    setOpsUser(ops_user);
    return ops_user;
  };

  const resend2fa = async (challenge) => {
    const res = await axios.post(`${OPS_BASE}/auth/resend-2fa`, { challenge }, { withCredentials: true });
    return res.data;
  };

  const logout = useCallback(async () => {
    await opsRequest('POST', '/auth/logout').catch(() => {});
    removeToken();
    setOpsUser(null);
  }, []);

  // opsAxios — token read from sessionStorage fresh on every call
  const opsAxios = useCallback(({ url, method = 'GET', data, params } = {}) => {
    return opsRequest(method, url, data, params);
  }, []);

  return (
    <OpsAuthContext.Provider value={{
      opsUser, loading, login, verify2fa, resend2fa, logout, opsAxios,
      OPS_API: OPS_BASE,
    }}>
      {children}
    </OpsAuthContext.Provider>
  );
}

export function useOpsAuth() {
  const ctx = useContext(OpsAuthContext);
  if (!ctx) throw new Error('useOpsAuth must be inside OpsAuthProvider');
  return ctx;
}
